import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import LicensedSponsorBadge from "./LicensedSponsorBadge";
import { AlertCircle, BriefcaseBusiness, Loader2 } from "lucide-react";

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || "https://jobzam-backend.onrender.com";

function formatSalary(min, max) {
  if (!min && !max) return "Salary not listed";
  if (min && max) return `GBP ${min.toLocaleString()} - GBP ${max.toLocaleString()}`;
  if (min) return `From GBP ${min.toLocaleString()}`;
  return `Up to GBP ${max.toLocaleString()}`;
}

export default function JobDetailsPage() {
  const { id } = useParams();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isApplied, setIsApplied] = useState(false);

  useEffect(() => {
    const loadJob = async () => {
      try {
        setErr("");
        setLoading(true);

        const res = await fetch(`${BACKEND}/jobs/${id}`);
        if (!res.ok) throw new Error("Failed to load job details");

        const data = await res.json();
        setJob(data);
      } catch (e) {
        setErr(e.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id]);

  useEffect(() => {
    if (job?._action) {
      setIsSaved(!!job._action.saved);
      setIsApplied(!!job._action.applied);
    }
  }, [job]);

  const updateJobAction = async (payload) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) throw new Error("Not logged in");

    const res = await fetch(`${BACKEND}/jobs/action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        job_id: id,
        ...payload,
      }),
    });

    if (!res.ok) throw new Error("Action update failed");
    return await res.json();
  };

  const toggleSave = async () => {
    try {
      setSaving(true);
      const next = !isSaved;
      setIsSaved(next);
      await updateJobAction({ saved: next });
    } catch (e) {
      setIsSaved((current) => !current);
      alert(e.message || "Failed to update save");
    } finally {
      setSaving(false);
    }
  };

  const toggleApplied = async () => {
    try {
      setApplying(true);
      const next = !isApplied;
      setIsApplied(next);
      await updateJobAction({ applied: next });
    } catch (e) {
      setIsApplied((current) => !current);
      alert(e.message || "Failed to update applied status");
    } finally {
      setApplying(false);
    }
  };

  const applyExternally = async () => {
    if (!job?.job_url) return;

    try {
      setApplying(true);

      if (!isApplied) {
        setIsApplied(true);
        await updateJobAction({ applied: true });
      }

      window.open(job.job_url, "_blank", "noreferrer");
    } catch (e) {
      alert(e.message || "Failed to track apply");
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <div role="status" className="flex min-h-72 flex-col items-center justify-center bg-slate-50"><Loader2 className="mb-3 h-7 w-7 animate-spin text-blue-600" aria-hidden="true" /><p className="font-semibold text-slate-600">Loading job...</p></div>;
  if (err) return <div role="alert" className="mx-auto my-12 max-w-xl rounded-3xl border border-rose-200 bg-rose-50 p-8 text-center"><AlertCircle className="mx-auto h-8 w-8 text-rose-600" aria-hidden="true" /><h1 className="mt-4 text-2xl font-black text-slate-950">We couldn’t load this job</h1><p className="mt-2 text-slate-600">Please return to the jobs portal and try again.</p><Link to="/jobs" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white">Back to Jobs</Link></div>;
  if (!job) return <div className="mx-auto my-12 max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center"><BriefcaseBusiness className="mx-auto h-8 w-8 text-blue-600" aria-hidden="true" /><h1 className="mt-4 text-2xl font-black text-slate-950">Job not found</h1><p className="mt-2 text-slate-600">This listing may no longer be available.</p><Link to="/jobs" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white">Back to Jobs</Link></div>;

  const hasApply = !!(job.job_url && String(job.job_url).trim());

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-5xl">
      <div className="mb-4">
        <Link to="/jobs" className="text-indigo-600 font-semibold text-sm">
          Back to Jobs
        </Link>
      </div>

      <div className="border border-slate-200 rounded-3xl p-5 sm:p-8 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.07)]">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{job.title}</h1>

            <p className="text-slate-600 mt-2">
              <span className="font-semibold">{job.company}</span> - {job.location} -{" "}
              {job.country}
            </p>

            <p className="text-slate-600 mt-1">
              {formatSalary(job.salary_min, job.salary_max)}
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {job.company ? <LicensedSponsorBadge companyName={job.company} /> : null}

              {isApplied ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
                  Applied
                </span>
              ) : null}

              {isSaved ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs font-semibold">
                  Saved
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 md:w-auto">
            {hasApply ? (
              <button
                onClick={applyExternally}
                disabled={applying}
                className={
                  "px-4 py-2 rounded-lg font-semibold transition text-sm text-center " +
                  (isApplied
                    ? "bg-slate-100 text-slate-800 hover:bg-slate-200"
                    : "bg-indigo-600 text-white hover:bg-indigo-700")
                }
              >
                {applying ? "Opening..." : isApplied ? "Applied" : "Apply Externally"}
              </button>
            ) : (
              <button
                disabled
                className="px-4 py-2 rounded-lg bg-slate-300 text-white font-semibold text-sm cursor-not-allowed"
              >
                No Apply Link
              </button>
            )}

            <button
              onClick={toggleSave}
              disabled={saving}
              className={
                "px-4 py-2 rounded-lg font-semibold transition text-sm " +
                (isSaved
                  ? "bg-slate-100 text-slate-800 hover:bg-slate-200"
                  : "bg-yellow-500 text-white hover:bg-yellow-600")
              }
            >
              {saving ? "Updating..." : isSaved ? "Unsave" : "Save Job"}
            </button>

            <button
              onClick={toggleApplied}
              disabled={applying}
              className={
                "px-4 py-2 rounded-lg font-semibold transition text-sm " +
                (isApplied
                  ? "bg-slate-100 text-slate-800 hover:bg-slate-200"
                  : "bg-blue-600 text-white hover:bg-blue-700")
              }
            >
              {applying
                ? "Updating..."
                : isApplied
                ? "Unmark Applied"
                : "Mark as Applied"}
            </button>
          </div>
        </div>

        <hr className="my-4" />

        <h2 className="text-lg font-semibold text-slate-900 mb-2">
          Job Description
        </h2>

        <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
          {job.description || "No description provided."}
        </div>

        <div className="mt-4 text-xs text-slate-500">
          Source: {job.source || "unknown"}
        </div>
      </div>
      </div>
    </div>
  );
}
